import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
} from "react";
import {
  Account,
  BillingMethod,
  Channel,
  CloudConnector,
  Consumption,
  EntityType,
} from "@services/account/types";
import { useSimpleReducer } from "@common/hooks";
import { useErrorNotification } from "@common/components/Notification";
import { getAccounts, getChannels } from "@services/account";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { useParams } from "react-router-dom";
import { canViewAccount } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { FormContext } from "./formContext";

export interface IFieldValueContext {
  channels?: Channel[];
  accounts?: Account[];
  tenantAccounts?: Account[];
  billingMethods: { id: BillingMethod; name: string }[];
  fetchChannels: () => void;
  fetchAccounts: (id: string) => void;
  cloudConnectors: { id: CloudConnector; name: string }[];
  consumptions: { id: Consumption; name: string }[];
  entityTypes?: { id: EntityType; name: string }[];
}

export const billingMethods = [
  { id: BillingMethod.MasterAccount, name: "Master Account Billing" },
  { id: BillingMethod.MeetingLevel, name: "Meeting Level Billing" },
  { id: BillingMethod.SiteBySite, name: "Site By Site Billing" },
];

export const cloudConnectors = [
  { id: CloudConnector.AVIHosted, name: "AVI Hosted" },
  { id: CloudConnector.ONPREM, name: "ONPREM CC" },
];

export const consumptions = [
  { id: Consumption.ManagedService, name: "Managed Service" },
  { id: Consumption.SaaS, name: "SaaS" },
];

export const entityTypes = [
  { id: EntityType.Tenant, name: "Tenant" },
  { id: EntityType.Account, name: "Account" },
];

export const FieldValueContext = createContext<IFieldValueContext>({
  fetchChannels: () => {},
  fetchAccounts: () => {},
  billingMethods,
  cloudConnectors,
  consumptions,
  entityTypes,
});

export const FieldValueProvider = (props: PropsWithChildren<{}>) => {
  const [state, dispatch] = useSimpleReducer<IFieldValueContext>({
    fetchChannels: () => {},
    fetchAccounts: () => {},
    billingMethods,
    cloudConnectors,
    consumptions,
    entityTypes,
  });
  const [userSelectedAccounts, , allAccounts] = useUserSelectedAccounts();
  const [user] = useAuthenticatedUser();
  const { accountid } = useParams();
  const dispatchError = useErrorNotification();
  const fetchChannels = async () => {
    try {
      dispatch({
        //need view Account permission for fetch this Endpoint
        channels: canViewAccount(user)
          ? (await getChannels()).sort((channelA, channelB) =>
              (channelA.name ?? "")
                .toLowerCase()
                .localeCompare((channelB.name ?? "").toLowerCase())
            )
          : undefined,
      });
    } catch (e) {
      dispatchError(e);
    }
  };

  const fetchAccounts = async (tenantId: string) => {
    try {
      dispatch({
        accounts: (
          await getAccounts(
            { "tenantAccount.id": tenantId },
            { queryType: "provisioning" }
          )
        ).list
          .filter(({ id }) => {
            //If the account is selected, not the current one consulted and doesn't have a parent
            if (
              userSelectedAccounts?.includes(id) &&
              id !== accountid &&
              // parentAccount isnt present all the time in provisioning endpoint
              allAccounts.find((acc) => acc.id === id)?.parentAccount ===
                undefined
            )
              return true;
            //Find if the account is a parent of one of current account consulted
            if (
              allAccounts.find((acc) => acc.id === accountid)?.parentAccount
                ?.id === id
            )
              return true;
            return false;
          })
          .sort((accountA, accountB) =>
            (accountA.name ?? "")
              .toLowerCase()
              .localeCompare((accountB.name ?? "").toLowerCase())
          ),
      });
    } catch (e) {
      dispatchError(e);
    }
  };

  const fetchTenantAccounts = async () => {
    try {
      dispatch({
        tenantAccounts: (
          await getAccounts(
            { entityType: "Tenant" },
            { queryType: "provisioning" }
          )
        ).list,
      });
    } catch (e) {
      dispatchError(e);
    }
  };

  useEffect(() => {
    fetchChannels();
    fetchTenantAccounts();
  }, []);

  return (
    <FieldValueContext.Provider
      value={{ ...state, fetchChannels, fetchAccounts }}
    >
      {props.children}
    </FieldValueContext.Provider>
  );
};
